import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { FaCarAlt, FaPlusCircle, FaWallet } from "react-icons/fa";
import api from "../../setupMiddleware";
import useApiLoader from "../../Utils/useApiLoader";
import Loader from "../Loader/Loader";
import { motion, AnimatePresence } from "framer-motion";
import Pagination from "../Common/Pagination";

export default function Transactions() {
  const loading = useApiLoader();
  const [transactions, setTransactions] = useState([]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchTransaction = async () => {
    try {
      const res = await api.get("/api/admin/transactions");
      if (res.status === 200) {
        setTransactions(res.data?.transactions || []);
      }
    } catch (err) {
      console.error("Error fetching transactions", err);
    }
  };

  useEffect(() => {
    fetchTransaction();
  }, []);

  const totalPages = Math.max(1, Math.ceil(transactions.length / pageSize));

  const currentSlice = useMemo(() => {
    const start = (page - 1) * pageSize;
    return transactions.slice(start, start + pageSize);
  }, [transactions, page, pageSize]);

  return (
    <>
      {loading && <Loader opacity={50} />}

      <main className="max-w-5xl mx-auto py-6 px-3 text-gray-800">
        <div className="bg-white/95 border border-gray-100 rounded-2xl shadow-[0_2px_6px_rgba(0,0,0,0.06)] mt-1 md:mt-6 mb-4 pb-4 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-brand/10 text-brand shadow-inner">
                <FaWallet className="w-4 h-4" />
              </div>
              <h2 className="font-semibold text-xs md:text-sm text-gray-800 tracking-wide">
                All Transactions
              </h2>
            </div>
            {/* Row Count */}
            <div className="flex items-center gap-2 text-xs md:text-sm">
              <span className="text-xs text-gray-500">Rows:</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="border rounded px-2 py-1 text-xs md:text-sm bg-white"
              >
                {[5, 10, 20, 50].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* List */}
          <div className="p-5 divide-y divide-gray-100">
            <AnimatePresence mode="wait">
              {currentSlice.length > 0 ? (
                currentSlice.map((tx) => (
                  <motion.div
                    key={tx.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.25 }}
                    className="flex flex-row items-center justify-between gap-3 py-3 px-2 hover:bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-9 h-9 flex items-center justify-center rounded-xl ${
                          tx.type === "credit"
                            ? "bg-green-100 text-green-600"
                            : "bg-rose-100 text-rose-600"
                        } shadow-inner`}
                      >
                        {tx.type === "credit" ? (
                          <FaPlusCircle className="w-5 h-5" />
                        ) : (
                          <FaCarAlt className="w-5 h-5" />
                        )}
                      </div>

                      <div>
                        <p className="font-medium text-gray-800 text-xs md:text-sm">
                          {tx.type === "debit"
                            ? `${tx.request_type} ${tx.transaction_name}`
                            : tx.transaction_name}
                        </p>

                        <p className="text-[11px] text-gray-600">
                          {tx.user_name} <br />
                          <span className="text-gray-500">{tx.email}</span>
                        </p>

                        <p className="text-[11px] text-gray-400">
                          {moment(tx.transaction_time).format(
                            "DD MMM YYYY, hh:mm A"
                          )}
                        </p>
                      </div>
                    </div>

                    <span
                      className={`font-semibold text-xs md:text-sm px-3 py-1.5 rounded-full text-right ${
                        tx.type === "credit"
                          ? "text-green-700 bg-green-50"
                          : "text-rose-600 bg-rose-50"
                      }`}
                    >
                      {tx.type === "credit"
                        ? `+ ₹${Number(tx.amount).toLocaleString("en-IN")}`
                        : `- ₹${Math.abs(tx.amount).toLocaleString("en-IN")}`}
                    </span>
                  </motion.div>
                ))
              ) : (
                <div className="text-center py-5 text-gray-400 text-xs md:text-sm">
                  No transactions found
                </div>
              )}
            </AnimatePresence>
          </div>

          <Pagination current={page} total={totalPages} onChange={setPage} />
        </div>
      </main>
    </>
  );
}
